import config from "../../config.js";
import axios from "axios";

export const handleApiError = (error, context = "") => {
  if (error.response) {
    console.warn(`${context} -> API responded with status ${error.response.status}`, error.response.data);
  } else if (error.request) {
    console.error(`${context} -> No response received from server`, error.request);
  } else {
    console.error(`${context} -> Request setup error`, error.message);
  }
};

const formatDeviceTypeDescription = (deviceType) =>
  String(deviceType || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");

export const getDeviceTypes = async () => {
  const resp = await axios.get(`${config.baseURL}/device/types`, { withCredentials: true });
  const types = resp.data?.data || resp.data?.deviceTypes || resp.data?.types || resp.data;
  return Array.isArray(types) ? types : [];
};

const ensureDeviceTypeId = async (deviceType) => {
  if (!deviceType) return null;
  if (!isNaN(Number(deviceType))) return Number(deviceType);

  const normalizedName = String(deviceType).trim();
  const deviceTypes = await getDeviceTypes();
  const existingDeviceType = deviceTypes.find(
    (type) => String(type.name).toUpperCase() === normalizedName.toUpperCase(),
  );

  if (existingDeviceType) {
    return existingDeviceType.id;
  }

  try {
    const resp = await axios.post(
      `${config.baseURL}/device/types`,
      {
        name: normalizedName,
        description: formatDeviceTypeDescription(normalizedName),
      },
      { withCredentials: true },
    );
    return resp.data?.data?.id || null;
  } catch (error) {
    if (error.response?.status !== 400) {
      throw error;
    }

    const refreshedDeviceTypes = await getDeviceTypes();
    const createdDeviceType = refreshedDeviceTypes.find(
      (type) => String(type.name).toUpperCase() === normalizedName.toUpperCase(),
    );
    if (createdDeviceType) return createdDeviceType.id;
    throw error;
  }
};

const prepareDevicePayload = async (deviceData) => {
  const payload = { ...deviceData };
  const deviceTypeId = await ensureDeviceTypeId(payload.deviceTypeId || payload.deviceType);

  if (deviceTypeId) {
    payload.deviceTypeId = deviceTypeId;
  } else {
    delete payload.deviceTypeId;
  }

  delete payload.deviceType;
  return payload;
};

export const uploadNfcCardsCsv = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const resp = await axios.post(
    `${config.baseURL}/device/nfc-cards/upload`,
    formData,
    {
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" },
    },
  );

  return resp.data;
};

export const createTrackingDevice = async (deviceData) => {
  try {
    const payload = await prepareDevicePayload(deviceData);
    const resp = await axios.post(`${config.baseURL}/device`, payload, { withCredentials: true });
    return resp.data;
  } catch (error) {
    handleApiError(error, "createTrackingDevice");
    throw error;
  }
};

export const getAllTrackingDevices = async () => {
  try {
    const resp = await axios.get(`${config.baseURL}/device`, { withCredentials: true });
    return resp.data;
  } catch (error) {
    handleApiError(error, "getAllTrackingDevices");
    return [];
  }
};

const buildUploadUrl = (location) =>
  location ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(location)}` : null;

const normalizeVehicle = (vehicle) =>
  vehicle
    ? {
        ...vehicle,
        registrationNumber: vehicle.registrationNumber || vehicle.VehicleNumber || "",
        model: vehicle.model || vehicle.Model || "",
        VehicleNumber: vehicle.VehicleNumber || vehicle.registrationNumber || "",
        Model: vehicle.Model || vehicle.model || "",
        siteId: vehicle.siteId || vehicle.site?.siteId || vehicle.site?.id || "",
      }
    : null;

const normalizeStop = (stop) =>
  stop
    ? {
        ...stop,
        stopId: stop.stopId || "",
        name: stop.name || "",
        latitude: stop.latitude,
        longitude: stop.longitude,
        landmark: stop.landmark || "",
        siteId: stop.siteId || stop.site?.siteId || stop.site?.id || "",
        site: stop.site || null,
      }
    : null;

const toDateInputValue = (dateValue) =>
  dateValue ? String(dateValue).split("T")[0] : "";

const normalizeSimStatus = (status) => {
  const normalizedStatus = String(status || "INACTIVE").trim().toUpperCase();
  return normalizedStatus === "ACTIVE" ? "ACTIVE" : "INACTIVE";
};

const getDeviceOwnerValue = (formData) => {
  if (formData.ownedByType === "Customer") {
    return formData.ownedBy || formData.organizationName || null;
  }
  return formData.ownedBy || "Irisidea";
};

const getInstallationPhotoNumber = (name) => {
  const match = /^InstallationPhoto(\d*)$/.exec(String(name || ""));
  if (!match) return null;
  return match[1] ? Number(match[1]) : 0;
};

const normalizeDeviceForForm = (device = {}) => {
  const documents = Array.isArray(device.documents) ? device.documents : [];
  const deviceImage = documents.find((doc) => doc.name === "DeviceImage");
  const installationPhotos = documents
    .map((photo) => ({
      photo,
      photoNumber: getInstallationPhotoNumber(photo.name),
    }))
    .filter((item) => item.photoNumber !== null)
    .sort((a, b) => a.photoNumber - b.photoNumber || new Date(a.photo.createdAt) - new Date(b.photo.createdAt))
    .map(({ photo, photoNumber }, index) => ({
      ...photo,
      photoNumber: photoNumber || index + 1,
      url: buildUploadUrl(photo.location),
    }));

  const vehicle = normalizeVehicle(device.Vehicle || device.vehicle || device.assignedVehicle);
  const stop = normalizeStop(device.Stop || device.stop || device.assignedStop);
  const simCard = device.SimCard || device.simCard || null;
  const deviceType =
    typeof device.deviceType === "object"
      ? device.deviceType?.name
      : device.deviceType || device.type || "";
  const organization = device.organization || vehicle?.organization || stop?.organization || null;
  const assignmentType = device.assignmentType || (stop ? "STOP" : "VEHICLE");

  return {
    id: device.id,
    deviceId: device.deviceId || "",
    deviceType,
    sensorType: device.sensorType || "",
    unit: device.unit || "",
    minValue: device.minValue ?? "",
    maxValue: device.maxValue ?? "",
    imeiNumber: device.imeiNumber || "",
    model: device.model || "",
    vendorBrand: device.vendorBrand || "",
    ownedBy: device.ownedBy || "Irisidea",
    workingCondition: device.workingCondition || "",
    status: device.status || "",
    installationDate: device.installationDate ? device.installationDate.split("T")[0] : "",
    assignmentType,
    organizationName: device.organizationName || organization?.name || "",
    organizationId: device.organizationId || organization?.id || "",
    city: device.city || organization?.city || "",
    site: device.site || vehicle?.siteId || vehicle?.site?.siteId || vehicle?.site?.id || "",
    executiveFullName: device.executiveFullName || "",
    executivePhoneNumber: device.executivePhoneNumber || "",
    executiveEmail: device.executiveEmail || "",
    remarks: device.remarks || "",
    SimCard: simCard
      ? {
          id: simCard.id,
          simNumber: simCard.simNumber || "",
          imeiNumber: simCard.imeiNumber || "",
          currentPlan: simCard.currentPlan || "",
          planExpiry: toDateInputValue(simCard.planExpiry),
          provider: simCard.provider || "",
          ownedBy: simCard.simOwner || simCard.ownedBy || "",
          simActivationDate: toDateInputValue(
            simCard.simActivationDate ||
              simCard.activationDate ||
              simCard.activatedAt,
          ),
          simStatus: normalizeSimStatus(simCard.simStatus || simCard.status),
        }
      : null,
    Vehicle: vehicle,
    Stop: stop,
    stopId: device.stopId || stop?.stopId || "",
    stopName: device.stopName || stop?.name || "",
    deviceImageUrl: buildUploadUrl(deviceImage?.location),
    Document: installationPhotos,
    specifications: device.specifications || [],
  };
};

export const getTrackingDeviceById = async (id) => {
  try {
    const resp = await axios.get(`${config.baseURL}/device/${id}`, { withCredentials: true });
    return { data: normalizeDeviceForForm(resp.data?.data || resp.data || {}) };
  } catch (error) {
    handleApiError(error, `getTrackingDeviceById(${id})`);
    throw error;
  }
};

export const updateTrackingDevice = async (deviceData) => {
  try {
    const { id, ...updateData } = deviceData;
    if (!id) {
      throw new Error("Device ID is required for update");
    }
    const payload = await prepareDevicePayload(updateData);
    const resp = await axios.put(`${config.baseURL}/device/${id}`, payload, { withCredentials: true });
    return resp.data;
  } catch (error) {
    handleApiError(error, "updateTrackingDevice");
    throw error;
  }
};

export const deleteTrackingDevice = async (id) => {
  try {
    const resp = await axios.delete(`${config.baseURL}/device/${id}`, { withCredentials: true });
    return resp.data;
  } catch (error) {
    handleApiError(error, `deleteTrackingDevice(${id})`);
    throw error;
  }
};

export const assignDeviceToVehicle = async (trackingDeviceId, vehicleId) => {
  try {
    const resp = await axios.post(
      `${config.baseURL}/device/assign`,
      { trackingDeviceId, vehicleId },
      { withCredentials: true },
    );
    return resp.data;
  } catch (error) {
    handleApiError(error, `assignDeviceToVehicle(${trackingDeviceId}, ${vehicleId})`);
    throw error;
  }
};

export const getTrackingDevicesByOrgIdIndependent = async (organizationId) => {
  try {
    const resp = await axios.get(
      `${config.baseURL}/device/organization/${organizationId}`,
      { withCredentials: true },
    );
    return resp.data;
  } catch (error) {
    handleApiError(error, "getTrackingDevicesByOrgId");
    return [];
  }
};

export const getDeviceMonitorLatest = async (params = {}) => {
  try {
    const resp = await axios.get(`${config.baseURL}/device/monitor/latest`, {
      params,
      withCredentials: true,
    });
    return resp.data;
  } catch (error) {
    handleApiError(error, "getDeviceMonitorLatest");
    throw error;
  }
};

export const getDeviceMonitorLatestAll = async () => {
  try {
    const resp = await axios.get(`${config.baseURL}/device/monitor/latest/all`, {
      withCredentials: true,
    });
    return resp.data;
  } catch (error) {
    handleApiError(error, "getDeviceMonitorLatestAll");
    throw error;
  }
};

export const getDeviceHistory = async (trackingDeviceId, params = {}) => {
  try {
    const resp = await axios.get(
      `${config.baseURL}/device/${trackingDeviceId}/history`,
      {
        params,
        withCredentials: true,
      },
    );
    return resp.data;
  } catch (error) {
    handleApiError(error, `getDeviceHistory(${trackingDeviceId})`);
    throw error;
  }
};

export const getDeviceMonitorAlerts = async (params = {}) => {
  try {
    const resp = await axios.get(`${config.baseURL}/device/alerts`, {
      params,
      withCredentials: true,
    });
    return resp.data;
  } catch (error) {
    handleApiError(error, "getDeviceMonitorAlerts");
    throw error;
  }
};

export const getDeviceStreamUrl = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length) {
      searchParams.set(key, value.join(","));
    } else if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, value);
    }
  });
  return `${config.baseURL}/device/stream?${searchParams.toString()}`;
};

export const formatDeviceData = (formData, isUpdate = false, existingSimCardId = null) => {
  // Mounted On/Attached To "Other" is disabled in create/update. Normalize any
  // legacy value before sending payloads.
  const assignmentType =
    formData.assignmentType === "OTHER"
      ? "VEHICLE"
      : formData.assignmentType || "VEHICLE";
  const isStopAssignment = assignmentType === "STOP";
  const specifications = (formData.specifications || [])
    .map((specification, index) => ({
      id: String(specification?.id || `spec-${index + 1}`),
      name: specification?.name?.trim() || "",
      value: specification?.value || "",
    }))
    .filter((specification) => specification.name);

  const deviceData = {
    deviceId: formData.deviceId,
    deviceType: formData.deviceType || null,
    sensorType: formData.deviceType === "SENSOR" ? formData.sensorType || null : null,
    unit: formData.deviceType === "SENSOR" ? formData.unit || null : null,
    minValue:
      formData.deviceType === "SENSOR" && formData.minValue !== ""
        ? Number(formData.minValue)
        : null,
    maxValue:
      formData.deviceType === "SENSOR" && formData.maxValue !== ""
        ? Number(formData.maxValue)
        : null,
    imeiNumber: formData.imeiNumber || null,
    model: formData.model || null,
    vendorBrand: formData.vendorBrand || null,
    ownedBy: getDeviceOwnerValue(formData),
    workingCondition: formData.workingCondition || null,
    status: formData.status,
    installationDate: formData.installationDate || null,
    assignmentType,
    organizationName: formData.organizationName || null,
    organizationId: formData.organizationId || formData.ownedByOrganizationId || null,
    city: formData.city || null,
    site: formData.site || null,
    vehicleId: isStopAssignment ? null : formData.vehicle?.id || null,
    vehicleName: isStopAssignment ? null : formData.vehicle?.name || null,
    vehicleRegistrationNumber: isStopAssignment ? null : formData.vehicleRegistrationNumber || null,
    vehicleModel: isStopAssignment ? null : formData.vehicleModel || null,
    stopId: isStopAssignment ? formData.stop?.id || null : null,
    stopCode: isStopAssignment ? formData.stop?.stopId || formData.stopId || null : null,
    stopName: isStopAssignment ? formData.stop?.name || formData.stopName || null : null,
    stop: isStopAssignment ? formData.stop || null : null,
    vehicle: isStopAssignment ? null : formData.vehicle || null,
    executiveFullName: formData.executiveFullName || null,
    executivePhoneNumber: formData.executivePhoneNumber || null,
    executiveEmail: formData.executiveEmail || null,
    remarks: formData.remarks || null,
    specifications,
    simCard: {
      imeiNumber: formData.simCard?.simImei || null,
      provider: formData.simCard?.provider || null,
      simOwner: formData.simCard?.ownedBy || null,
      simActivationDate: formData.simCard?.simActivationDate
        ? new Date(formData.simCard.simActivationDate).toISOString()
        : null,
      simStatus: normalizeSimStatus(formData.simCard?.simStatus),
      planExpiry: formData.simCard?.nextRechargeDate
        ? new Date(formData.simCard.nextRechargeDate).toISOString()
        : null,
      simNumber: formData.simCard?.mobileNumber || null,
    },
  };

  if (isUpdate) {
    deviceData.id = formData.id;
    if (existingSimCardId) {
      deviceData.simCard.id = existingSimCardId;
    }
  }

  return deviceData;
};

export const validateDevice = (deviceData) => {
  const errors = [];

  if (!deviceData.deviceId?.trim()) {
    errors.push("Device ID is required");
  }

  if (!deviceData.status?.trim()) {
    errors.push("Status is required");
  }

  if (!deviceData.workingCondition?.trim()) {
    errors.push("Working Condition is required");
  }

  if (deviceData.imeiNumber?.trim() && !isValidIMEI(deviceData.imeiNumber.trim())) {
    errors.push("Device IMEI number must be numeric");
  }

  if (deviceData.minValue !== null && Number.isNaN(Number(deviceData.minValue))) {
    errors.push("Min threshold must be numeric");
  }

  if (deviceData.maxValue !== null && Number.isNaN(Number(deviceData.maxValue))) {
    errors.push("Max threshold must be numeric");
  }

  if (
    deviceData.minValue !== null &&
    deviceData.maxValue !== null &&
    !Number.isNaN(Number(deviceData.minValue)) &&
    !Number.isNaN(Number(deviceData.maxValue)) &&
    Number(deviceData.minValue) > Number(deviceData.maxValue)
  ) {
    errors.push("Max threshold must be greater than or equal to min threshold");
  }

  if (deviceData.simCard?.imeiNumber?.trim() && !isValidIMEI(deviceData.simCard.imeiNumber.trim())) {
    errors.push("SIM IMEI must be numeric");
  }

  if (deviceData.executivePhoneNumber?.trim() && !isValidPhoneNumber(deviceData.executivePhoneNumber.trim())) {
    errors.push("Executive phone number format is invalid");
  }

  if (deviceData.simCard?.simNumber?.trim() && !isValidPhoneNumber(deviceData.simCard.simNumber.trim())) {
    errors.push("SIM mobile number format is invalid");
  }

  if (deviceData.executiveEmail?.trim() && !isValidEmail(deviceData.executiveEmail.trim())) {
    errors.push("Executive email format is invalid");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export { isValidIMEI, isValidPhoneNumber, isValidEmail };

export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "INSTALLED":
      return "green";
    case "PENDING":
      return "orange";
    case "REMOVED":
      return "red";
    default:
      return "gray";
  }
};

export const getConditionColor = (condition) => {
  switch (condition?.toUpperCase()) {
    case "WORKING":
      return "green";
    case "NOT_WORKING":
      return "red";
    case "NEEDS_REPAIR":
      return "orange";
    default:
      return "gray";
  }
};

export const WORKING_CONDITIONS = [
  { value: "WORKING", label: "Working" },
  { value: "NOT_WORKING", label: "Not Working" },
  { value: "NEEDS_REPAIR", label: "Needs Repair" },
];

export const INSTALL_STATUS = [
  { value: "INSTALLED", label: "Installed" },
  { value: "PENDING", label: "Not Installed" },
  { value: "REMOVED", label: "Removed" },
];

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidIMEI = (imei) => {
  const imeiRegex = /^[a-zA-Z0-9]+$/;
  return imeiRegex.test(imei);
};

const isValidPhoneNumber = (phone) => {
  const phoneRegex = /^[0-9a-zA-Z+\s\-()]{7,30}$/;
  return phoneRegex.test(phone);
};
